import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { roleConfig } from '../../config/rolePermissions';
import { useI18n } from '../../i18n/I18nContext';
import { getAiInboxCount } from '../../api/skills';
import { usePolling } from '../../hooks/usePolling';
import SidebarFrame from './SidebarFrame';
import { useStaffNav } from './shellNav';

// The brand pieces and the rail's open state live with the frame and the shell state; they are
// re-exported here because the splash screen, the top bar and the teacher shell import them from
// this file.
export { BrandMark, BrandLogo, ShellAvatar, initialsOf } from './SidebarFrame';
export { useSidebarOpen } from '../Layout/shellState';

/**
 * The staff rail (and, below 1024px, the drawer): destinations grouped and filtered by the staff
 * member's effective role. `nav` is the output of useStaffNav(); the shell passes it so the tab
 * bar shows the same destinations, and it is computed here when omitted.
 */
function Sidebar({ nav: navProp, mobileOpen = false, onMobileClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { staffRole } = usePermissions();
  const { t } = useI18n();
  const ownNav = useStaffNav();
  const nav = navProp || ownNav;

  // The AI-proposals badge sits on the Courses item, so a login without it (sales, the call centre)
  // never asks: /courses/ai-inbox-count answers them 403.
  const hasCourses = nav.groups.some((group) => group.items.some((item) => item.key === 'courses'));
  const [aiInboxCount, setAiInboxCount] = useState(0);
  const fetchAiInboxCount = useCallback(() => {
    getAiInboxCount()
      .then((res) => setAiInboxCount(typeof res?.count === 'number' ? res.count : 0))
      .catch(() => { /* non-fatal — the badge simply stays hidden */ });
  }, []);
  useEffect(() => { if (hasCourses) fetchAiInboxCount(); }, [hasCourses, fetchAiInboxCount]);
  usePolling(fetchAiInboxCount, 60000, hasCourses);

  useEffect(() => {
    onMobileClose?.();
    // Closing the drawer is tied to navigation only. The callback is omitted
    // from dependencies because the shell supplies a new inline function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const groups = nav.groups.map((group) => ({
    ...group,
    items: group.items.map((item) => (
      item.key === 'courses' && aiInboxCount > 0
        ? {
          ...item,
          badge: {
            text: aiInboxCount > 99 ? '99+' : aiInboxCount,
            label: t('nav.aiProposals', { count: aiInboxCount }),
            onActivate: () => navigate('/courses?aiProposed=true'),
          },
        }
        : item
    )),
  }));

  const name = user?.name || user?.email?.split('@')[0] || t('layout.userFallback');

  return (
    <SidebarFrame
      ariaLabel={t('layout.mainNav')}
      homeTo="/dashboard"
      tag={{ label: 'CRM' }}
      groups={groups}
      user={{ name, sub: t(roleConfig(staffRole).labelKey), avatar: user?.avatar, to: '/settings/mentor' }}
      mobileOpen={mobileOpen}
      onMobileClose={onMobileClose}
    />
  );
}

export default Sidebar;
